import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { AUTH } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const nav = useNavigate();
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error('Passwords do not match');
            return;
        }
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, password });
            toast.success('Password reset. Please sign in.');
            nav('/login');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid place-items-center bg-background px-6">
            <div className="w-full max-w-md">
                <div className="mb-8">
                    <h2 className="font-display text-3xl font-black tracking-tight mt-1">Set a new password</h2>
                </div>
                {!token ? (
                    <div className="rounded-sm border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                        Missing reset token in the URL.
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-5">
                        <div className="space-y-2">
                            <Label className="text-sm">New password</Label>
                            <Input
                                data-testid={AUTH.reset.passwordInput}
                                type="password"
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="rounded-sm h-11"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm">Confirm</Label>
                            <Input
                                data-testid={AUTH.reset.confirmInput}
                                type="password"
                                required
                                minLength={6}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="rounded-sm h-11"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid={AUTH.reset.submitButton}
                            disabled={loading}
                            className="w-full h-11 rounded-sm"
                        >
                            {loading ? <><Loader2 className="animate-spin mr-2" size={16} />Saving…</> : 'Reset password'}
                        </Button>
                        <Link to="/login" className="block text-sm text-muted-foreground hover:text-primary">
                            ← Back to sign in
                        </Link>
                    </form>
                )}
            </div>
        </div>
    );
}
